import React, { useEffect, useState } from "react";
import { req } from "../api";

export default function IntegrationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [webhookUrl, setWebhookUrl] = useState("");
  const [clientCallbackUrl, setClientCallbackUrl] = useState("");
  const [isActive, setIsActive] = useState(false);

  const [webhookSecretMasked, setWebhookSecretMasked] = useState("");
  const [lastWebhookTestAt, setLastWebhookTestAt] = useState(null);
  const [lastWebhookTestStatus, setLastWebhookTestStatus] = useState(null);
  const [lastWebhookTestOk, setLastWebhookTestOk] = useState(null);
  const [lastWebhookTestError, setLastWebhookTestError] = useState("");

  async function loadIntegration() {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const r = await req("/api/user/integration");
      if (!r.json?.ok) throw new Error(r.json?.error || "Failed to load integration settings");

      const i = r.json.integration || {};
      setWebhookUrl(i.webhookUrl || "");
      setClientCallbackUrl(i.clientCallbackUrl || "");
      setIsActive(Boolean(i.isActive));
      setWebhookSecretMasked(i.webhookSecretMasked || "");
      setLastWebhookTestAt(i.lastWebhookTestAt || null);
      setLastWebhookTestStatus(i.lastWebhookTestStatus ?? null);
      setLastWebhookTestOk(i.lastWebhookTestOk ?? null);
      setLastWebhookTestError(i.lastWebhookTestError || "");
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadIntegration();
  }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const r = await req("/api/user/integration/save", {
        method: "POST",
        body: {
          webhookUrl,
          clientCallbackUrl,
          isActive,
        },
      });

      if (!r.json?.ok) throw new Error(r.json?.error || "Failed to save integration settings");

      const i = r.json.integration || {};
      setWebhookSecretMasked(i.webhookSecretMasked || "");
      setLastWebhookTestAt(i.lastWebhookTestAt || null);
      setLastWebhookTestStatus(i.lastWebhookTestStatus ?? null);
      setLastWebhookTestOk(i.lastWebhookTestOk ?? null);
      setLastWebhookTestError(i.lastWebhookTestError || "");
      setSuccess("Integration settings saved");
    } catch (e2) {
      setError(String(e2?.message || e2));
    } finally {
      setSaving(false);
    }
  }

  async function sendTestWebhook() {
    setTesting(true);
    setError("");
    setSuccess("");

    try {
      const r = await req("/api/user/integration/test-webhook", {
        method: "POST",
        body: {},
      });

      if (!r.json?.ok) {
        const msg =
          r.json?.webhook?.error ||
          r.json?.error ||
          "Webhook test failed";
        throw new Error(msg);
      }

      await loadIntegration();
      setSuccess("Test webhook sent successfully");
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return <div style={{ maxWidth: 760, margin: "40px auto", padding: 16 }}>Loading...</div>;
  }

  return (
    <div style={{ maxWidth: 760, margin: "40px auto", padding: 16 }}>
      <h2>Merchant Integration</h2>
      <p style={{ color: "#555", marginTop: 0 }}>
        Here you can set the merchant webhook endpoint. After payment status updates arrive from Poteme,
        your platform will forward the event to this URL.
      </p>

      {error ? (
        <div style={{ color: "crimson", marginBottom: 12, whiteSpace: "pre-wrap" }}>{error}</div>
      ) : null}

      {success ? (
        <div style={{ color: "green", marginBottom: 12 }}>{success}</div>
      ) : null}

      <form onSubmit={save} style={{ display: "grid", gap: 14 }}>
        <label style={{ display: "grid", gap: 6 }}>
          <span>Webhook URL</span>
          <input
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://merchant-site.com/api/crypto/webhook"
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span>Client Callback URL</span>
          <input
            value={clientCallbackUrl}
            onChange={(e) => setClientCallbackUrl(e.target.value)}
            placeholder="https://merchant-site.com/payment/success"
          />
        </label>

        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <span>Integration active</span>
        </label>

        <div
          style={{
            border: "1px solid #ddd",
            borderRadius: 10,
            padding: 12,
            background: "#fafafa",
            display: "grid",
            gap: 8,
          }}
        >
          <div>
            <strong>Webhook secret:</strong>{" "}
            <span>{webhookSecretMasked || "Will be generated after first save"}</span>
          </div>

          <div>
            <strong>Last test:</strong>{" "}
            <span>{lastWebhookTestAt ? new Date(lastWebhookTestAt).toLocaleString() : "—"}</span>
          </div>

          <div>
            <strong>Last test status:</strong>{" "}
            <span>{lastWebhookTestStatus ?? "—"}</span>
          </div>

          <div>
            <strong>Last test result:</strong>{" "}
            <span>
              {lastWebhookTestOk === null ? "—" : lastWebhookTestOk ? "OK" : "FAILED"}
            </span>
          </div>

          {lastWebhookTestError ? (
            <div style={{ color: "crimson" }}>
              <strong>Last test error:</strong> {lastWebhookTestError}
            </div>
          ) : null}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save settings"}
          </button>

          <button
            type="button"
            onClick={sendTestWebhook}
            disabled={testing || !webhookUrl.trim()}
          >
            {testing ? "Sending..." : "Send test webhook"}
          </button>
        </div>
      </form>
    </div>
  );
}